// Shared form builders used by the client / lead / profile forms and the
// package modals. React + primitives + reference data only.
import { useState, useEffect, useRef } from "react";
import { packageExpiryOf } from "../domain";
import { fmt, sl, uid, DAYS, TIMES } from "../lib/format";
import { COUNTRY_CODES, COUNTRY_CURRENCY_MAP, COUNTRY_CODES_BY_LEN } from "../data/countries";
import { Inp, Sel, Btn, Icon, ConfirmButton } from "./primitives";

export const PhoneField=({value,onChange,error,label,hint,onCountryChange,onCurrencyChange,theme})=>{
  const isDark=theme?.dark;
  const mc=isDark?"#94a3b8":"#6b7280";
  const fieldBg=error&&!isDark?"#fef2f2":(isDark?"#0f172a":"#f9fafb"); // red-50 tint on error, matching sibling inputs
  const fieldText=isDark?"#f1f5f9":"#1f2937";
  const [open,setOpen]=useState(false);
  const [focused,setFocused]=useState(false);
  const [search,setSearch]=useState("");
  const baseBorder=error?"#f87171":(isDark?"#334155":"#e5e7eb");
  const inputBorder=focused&&!error?(theme?.primary||"#0f766e"):baseBorder;
  // Parse a stored value like "+91 9752132778" or "+1-242 5551234" into code + number.
  // Match the real code list (longest prefix wins) so a multi-segment code like
  // "+1-242" isn't misread as "+1" and slowly corrupted on every re-save.
  const parseValue=(v)=>{
    if(!v)return{code:"+91",num:""};
    const s=String(v).trim();
    const hit=COUNTRY_CODES_BY_LEN.find(c=>s.startsWith(c.code));
    if(hit)return{code:hit.code,num:s.slice(hit.code.length).replace(/^[\s-]+/,"")};
    const m=s.match(/^(\+\d{1,4})\s*(.*)/);
    return m?{code:m[1],num:m[2]}:{code:"+91",num:s};
  };
  const parsed=parseValue(value);
  const [countryCode,setCountryCode]=useState(parsed.code);
  const [number,setNumber]=useState(parsed.num);
  const dropRef=useRef(null);

  useEffect(()=>{
    const handler=(e)=>{if(dropRef.current&&!dropRef.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",handler);
    return()=>document.removeEventListener("mousedown",handler);
  },[]);

  const filtered=COUNTRY_CODES.filter(c=>
    c.country.toLowerCase().includes(search.toLowerCase())||
    c.code.includes(search)
  );

  const handleNumber=(e)=>{
    const val=e.target.value.replace(/[^\d\s-]/g,"");
    setNumber(val);
    onChange(`${countryCode} ${val}`.trim());
  };

  const selectCode=(code)=>{
    setCountryCode(code);
    setOpen(false);
    setSearch("");
    onChange(`${code} ${number}`.trim());
    const found=COUNTRY_CODES.find(c=>c.code===code);
    if(onCountryChange&&found)onCountryChange(found.country);
    const cc=COUNTRY_CURRENCY_MAP[code];
    if(onCurrencyChange&&cc?.currency)onCurrencyChange(cc.currency);
  };

  const selected=COUNTRY_CODES.find(c=>c.code===countryCode)||COUNTRY_CODES[0];

  return(
    <div className="flex flex-col gap-1">
      {label&&<label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>{label}</label>}
      <div className="flex gap-2 relative min-w-0" ref={dropRef}>
        {/* Country code button */}
        <button type="button" onClick={()=>setOpen(!open)}
          className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors flex-shrink-0"
          style={{minWidth:"90px",background:fieldBg,borderColor:baseBorder,color:fieldText}}>
          <span className="text-base">{selected.flag}</span>
          <span>{selected.code}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{opacity:.5}}><polyline points="6 9 12 15 18 9"/></svg>
        </button>
        {/* Number input — min-w-0 so its intrinsic size can't force the row past the card edge */}
        <input type="tel"
          className="flex-1 min-w-0 border rounded-xl px-4 py-2.5 text-sm focus:outline-none transition-colors"
          style={{background:fieldBg,borderColor:inputBorder,color:fieldText}}
          placeholder="98765 43210" value={number} onChange={handleNumber}
          onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)}/>
        {/* Dropdown */}
        {open&&(
          <div className="absolute top-full left-0 mt-1 w-72 rounded-2xl shadow-xl z-50 overflow-hidden border"
            style={{background:isDark?"#1e293b":"#fff",borderColor:isDark?"#334155":"#e5e7eb"}}>
            <div className="p-2 border-b" style={{borderColor:isDark?"#334155":"#f3f4f6"}}>
              <input autoFocus className="w-full px-3 py-2 text-sm rounded-xl focus:outline-none border"
                style={{background:isDark?"#0f172a":"#f9fafb",borderColor:isDark?"#334155":"#e5e7eb",color:fieldText}}
                placeholder="Search country..." value={search} onChange={e=>setSearch(e.target.value)}/>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filtered.map(c=>(
                <button key={c.code+c.country} type="button" onClick={()=>selectCode(c.code)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${isDark?"hover:bg-slate-700":"hover:bg-gray-50"}`}
                  style={{background:c.code===countryCode?(isDark?"#134e4a":"#f0fdf4"):"",color:fieldText}}>
                  <span className="text-base">{c.flag}</span>
                  <span className="text-sm flex-1">{c.country}</span>
                  <span className="text-xs font-medium" style={{color:mc}}>{c.code}</span>
                </button>
              ))}
              {filtered.length===0&&<p className="text-center text-sm py-4" style={{color:mc}}>No results</p>}
            </div>
          </div>
        )}
      </div>
      {error&&<p className="text-red-500 text-xs">{error}</p>}
      {hint&&!error&&<p className="text-xs" style={{color:mc}}>{hint}</p>}
    </div>
  );
};

export const PkgBuilder=({pkg,onChange,theme,currency="₹",profile})=>{
  const t=pkg.type||"monthly";
  const isDark=theme?.dark;
  const total=pkg.totalAmount||0;
  const templates=(profile?.packageTemplates||[]).sort((a,b)=>a.name.localeCompare(b.name));

  const expiry=packageExpiryOf({package:{...pkg,type:t}});

  const applyTemplate=(tpl)=>{
    const isBoth=tpl.packageStyle==="both";
    const isSession=tpl.packageStyle==="sessions"||isBoth;
    onChange({
      ...pkg,
      type:isSession?"template_sessions":"template_duration",
      templateId:tpl.id,
      templateName:tpl.name,
      templateSessions:tpl.sessions||0,
      templateDurationDays:tpl.durationDays||0,
      templateBoth:isBoth,
      totalAmount:tpl.amount||pkg.totalAmount||0,
      customClasses:isSession?tpl.sessions:0,
    });
  };

  return(
    <div className="space-y-3 p-4 rounded-xl border" style={{borderColor:isDark?"#334155":"#e5e7eb",background:isDark?"#0f172a":"#f9fafb"}}>
      <Sel label="Package Type" value={(t==="template_sessions"||t==="template_duration")&&pkg.templateId?"tpl_"+pkg.templateId:t} onChange={e=>{
        const val=e.target.value;
        // If selecting a template
        if(val.startsWith("tpl_")){
          const tpl=templates.find(tp=>"tpl_"+tp.id===val);
          if(tpl)applyTemplate(tpl);
          return;
        }
        onChange({...pkg,type:val,templateId:null,templateName:null});
      }} theme={theme}>
        <option value="none">No Package</option>
        {/* Templates at top */}
        {templates.length>0&&(
          <>
            <option disabled>── Your Packages ──</option>
            {templates.map(tpl=>(
              <option key={tpl.id} value={"tpl_"+tpl.id}>
                {tpl.name} · {tpl.packageStyle==="sessions"?`${tpl.sessions} ${tpl.sessions===1?(profile?.sessionLabel||"Session"):(profile?.sessionLabel||"Session")+"s"}`:`${tpl.durationDays} days`}{tpl.amount>0?` (${fmt(tpl.amount,currency)})`:""}
              </option>
            ))}
            <option disabled>── Standard ──</option>
          </>
        )}
        {["weekly","monthly","quarterly","half_yearly","yearly"].map(v=><option key={v} value={v}>{v==="half_yearly"?"Half Yearly":v.charAt(0).toUpperCase()+v.slice(1)}</option>)}
        <option value="custom">Custom (one-off)</option>
      </Sel>

      {/* Template selected — show details like custom package */}
      {(t==="template_sessions"||t==="template_duration")&&(
        <div className="space-y-3">
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1 flex-1">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{color:isDark?"#94a3b8":"#6b7280"}}>Package Name</label>
              <div className="border rounded-xl px-4 py-2.5 text-sm font-semibold"
                style={{background:isDark?"#0f172a":"#f9fafb",borderColor:isDark?"#334155":"#e5e7eb",color:theme?.primary||"#0f766e"}}>
                {pkg.templateName}
              </div>
            </div>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1" style={{width:"80px"}}>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{color:isDark?"#94a3b8":"#6b7280"}}>
                {t==="template_sessions"?sl(profile,2):"Days"}
              </label>
              <div className="border rounded-xl px-2 py-2.5 text-sm font-semibold text-center"
                style={{background:isDark?"#0f172a":"#f9fafb",borderColor:isDark?"#334155":"#e5e7eb",color:isDark?"#f1f5f9":"#1f2937"}}>
                {t==="template_sessions"?pkg.templateSessions:pkg.templateDurationDays}
              </div>
            </div>
            <div className="flex-1">
              <Inp label={`Total Amount (${currency})`} type="text" inputMode="decimal"
                value={pkg.totalAmount||""}
                onChange={e=>{const v=e.target.value.replace(/[^0-9.]/g,"");onChange({...pkg,totalAmount:parseFloat(v)||0});}}
                theme={theme}/>
            </div>
          </div>
        </div>
      )}

      {t==="weekly"&&(
        <div className="flex items-end gap-3">
          <div className="flex flex-col gap-1" style={{width:"80px"}}>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{color:isDark?"#94a3b8":"#6b7280"}}>Weeks</label>
            <input type="text" inputMode="numeric" maxLength={2}
              className="border rounded-xl px-2 py-2.5 text-sm focus:outline-none w-full text-center font-semibold"
              style={{background:isDark?"#0f172a":"#f9fafb",borderColor:isDark?"#334155":"#e5e7eb",color:isDark?"#f1f5f9":"#1f2937"}}
              placeholder="4" value={pkg.weeks||""}
              onChange={e=>{const w=e.target.value.replace(/[^0-9]/g,"");onChange({...pkg,weeks:parseInt(w)||1,totalAmount:(parseFloat(pkg.pricePerWeek)||0)*(parseInt(w)||1)});}}/>
          </div>
          <div className="flex-1">
            <Inp label={`Fee/Week (${currency})`} type="text" inputMode="decimal" value={pkg.pricePerWeek||""} onChange={e=>{const v=e.target.value.replace(/[^0-9.]/g,"");const p=parseFloat(v)||0;onChange({...pkg,pricePerWeek:v,totalAmount:p*(pkg.weeks||1)});}} theme={theme}/>
          </div>
        </div>
      )}
      {t==="monthly"&&<Inp label={`Fee/Month (${currency})`} type="text" inputMode="decimal" value={pkg.pricePerMonth||""} onChange={e=>{const v=e.target.value.replace(/[^0-9.]/g,"");onChange({...pkg,pricePerMonth:v,totalAmount:parseFloat(v)||0});}} theme={theme}/>}
      {t==="quarterly"&&<Inp label={`Fee/Quarter (${currency})`} type="text" inputMode="decimal" value={pkg.pricePerQuarter||""} onChange={e=>{const v=e.target.value.replace(/[^0-9.]/g,"");onChange({...pkg,pricePerQuarter:v,totalAmount:parseFloat(v)||0});}} theme={theme}/>}
      {t==="half_yearly"&&<Inp label={`Fee/6 Months (${currency})`} type="text" inputMode="decimal" value={pkg.pricePerHalfYear||""} onChange={e=>{const v=e.target.value.replace(/[^0-9.]/g,"");onChange({...pkg,pricePerHalfYear:v,totalAmount:parseFloat(v)||0});}} theme={theme}/>}
      {t==="yearly"&&<Inp label={`Fee/Year (${currency})`} type="text" inputMode="decimal" value={pkg.pricePerYear||""} onChange={e=>{const v=e.target.value.replace(/[^0-9.]/g,"");onChange({...pkg,pricePerYear:v,totalAmount:parseFloat(v)||0});}} theme={theme}/>}
      {t==="custom"&&(
        <div className="space-y-3">
          <Inp label="Package Name" value={pkg.customName||""} onChange={e=>onChange({...pkg,customName:e.target.value})} theme={theme}/>
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1" style={{width:"80px"}}>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{color:isDark?"#94a3b8":"#6b7280"}}>{sl(profile,2)}</label>
              <input type="text" inputMode="numeric" maxLength={3}
                className="border rounded-xl px-2 py-2.5 text-sm focus:outline-none w-full text-center font-semibold"
                style={{background:isDark?"#0f172a":"#f9fafb",borderColor:isDark?"#334155":"#e5e7eb",color:isDark?"#f1f5f9":"#1f2937"}}
                placeholder="20" value={pkg.customClasses||""}
                onChange={e=>onChange({...pkg,customClasses:e.target.value.replace(/[^0-9]/g,"")})}/>
            </div>
            <div className="flex-1">
              <Inp label={`Total Amount (${currency})`} type="text" inputMode="decimal" value={pkg.totalAmount||""} onChange={e=>{const v=e.target.value.replace(/[^0-9.]/g,"");onChange({...pkg,totalAmount:parseFloat(v)||0});}} theme={theme}/>
            </div>
          </div>
        </div>
      )}
      {t!=="none"&&(
        <Inp label="Package Start Date *" type="date" value={pkg.startDate||""} onChange={e=>onChange({...pkg,startDate:e.target.value})} theme={theme}
          error={t!=="none"&&!pkg.startDate?"Required — select a start date":""}/>
      )}
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold" style={{color:theme?.primary}}>Total: {fmt(total,currency)}</p>
        {expiry&&<p className="text-xs font-semibold" style={{color:isDark?"#94a3b8":"#6b7280"}}>Expires: {expiry}</p>}
      </div>
    </div>
  );
};

export const SchedBuilder=({schedule:initSchedule,onChange,clients,currentClientId,theme})=>{
  // Keep local state so day toggles don't bubble up and reset scroll
  const [rules,setRules]=useState(()=>initSchedule||[]);
  const isDark=theme?.dark;
  const mc=isDark?"#94a3b8":"#6b7280";
  const tc=isDark?"#f1f5f9":"#1f2937";
  const bc=isDark?"#334155":"#e5e7eb";
  const ibg=isDark?"#0f172a":"#f9fafb";
  const cb=isDark?"#1e293b":"#fff";

  // Sync up to parent without causing scroll jump — use ref to avoid stale closure
  const onChangeRef=useRef(onChange);
  onChangeRef.current=onChange;
  useEffect(()=>{ onChangeRef.current(rules); },[rules]);

  const addRule=()=>setRules(r=>[...r,{id:uid(),days:["Monday"],time:"09:00 AM",repeat:"every_week",duration:60}]);
  const removeRule=(id)=>setRules(r=>r.filter(x=>x.id!==id));
  const updateRule=(id,field,val)=>setRules(r=>r.map(x=>x.id===id?{...x,[field]:val}:x));
  const toggleDay=(ruleId,day)=>{
    setRules(r=>r.map(x=>{
      if(x.id!==ruleId)return x;
      const days=x.days.includes(day)?x.days.filter(d=>d!==day):[...x.days,day];
      return{...x,days:days.length?days:x.days};
    }));
  };

  const clashesFor=(rule)=>(clients||[]).filter(c=>{
    if(c.id===currentClientId)return false;
    return(c.schedule||[]).some(r2=>r2.time===rule.time&&r2.days?.some(d=>rule.days?.includes(d)));
  }).map(c=>c.name);

  const repeatLabel={every_week:"Every week",every_2_weeks:"Every 2 weeks",every_month:"Monthly"};
  const dayShort={Monday:"Mo",Tuesday:"Tu",Wednesday:"We",Thursday:"Th",Friday:"Fr",Saturday:"Sa",Sunday:"Su"};

  return(
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Class Schedule</label>
        <Btn variant="ghost" theme={theme} className="text-xs py-1 px-3" onClick={e=>{e.preventDefault();addRule();}}><Icon name="plus" size={13}/>Add Schedule</Btn>
      </div>
      {rules.length===0&&(
        <p className="text-xs py-2" style={{color:mc}}>No schedule set. Add recurring slots like "Every Mon & Wed at 9 AM".</p>
      )}
      {rules.map(rule=>{
        const clashes=clashesFor(rule);
        return(
          <div key={rule.id} className="rounded-xl border p-3 space-y-3" style={{background:ibg,borderColor:bc}}>
            <div>
              <p className="text-xs font-semibold mb-2" style={{color:mc}}>Select Days</p>
              <div className="flex gap-1.5 flex-wrap">
                {DAYS.map(d=>{
                  const sel=rule.days?.includes(d);
                  return(
                    <button key={d} type="button"
                      onMouseDown={e=>{e.preventDefault();e.stopPropagation();toggleDay(rule.id,d);}}
                      className="w-9 h-9 rounded-full text-xs font-bold transition-all select-none"
                      style={{background:sel?(theme?.primary||"#0f766e"):cb,color:sel?"#fff":mc,border:`1.5px solid ${sel?(theme?.primary||"#0f766e"):bc}`}}>
                      {dayShort[d]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              {/* Row 1: Time + Duration */}
              <div className="flex gap-2">
                <div className="flex flex-col gap-1 flex-1">
                  <p className="text-xs font-semibold" style={{color:mc}}>Time</p>
                  <select value={rule.time} onChange={e=>updateRule(rule.id,"time",e.target.value)}
                    className="border rounded-xl px-3 py-2 text-sm focus:outline-none"
                    style={{background:cb,borderColor:bc,color:tc}}>
                    {TIMES.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-semibold" style={{color:mc}}>Duration</p>
                  <select value={rule.duration||60} onChange={e=>updateRule(rule.id,"duration",parseInt(e.target.value))}
                    className="border rounded-xl px-3 py-2 text-sm focus:outline-none"
                    style={{background:cb,borderColor:bc,color:tc}}>
                    <option value={30}>30 min</option>
                    <option value={45}>45 min</option>
                    <option value={60}>1 hr</option>
                    <option value={75}>1h 15m</option>
                    <option value={90}>1.5 hr</option>
                    <option value={120}>2 hr</option>
                  </select>
                </div>
              </div>
              {/* Row 2: Repeat + Delete */}
              <div className="flex gap-2 items-end">
                <div className="flex flex-col gap-1 flex-1">
                  <p className="text-xs font-semibold" style={{color:mc}}>Repeats</p>
                  <select value={rule.repeat||"every_week"} onChange={e=>updateRule(rule.id,"repeat",e.target.value)}
                    className="border rounded-xl px-3 py-2 text-sm focus:outline-none"
                    style={{background:cb,borderColor:bc,color:tc}}>
                    <option value="every_week">Every week</option>
                    <option value="every_2_weeks">Every 2 weeks</option>
                    <option value="every_month">Monthly</option>
                  </select>
                </div>
                <ConfirmButton label={<Icon name="trash" size={14}/>} confirmLabel="Remove?" onConfirm={()=>removeRule(rule.id)} danger className="p-2 rounded-lg mb-0.5 flex-shrink-0" style={{color:"#ef4444",background:"#fef2f2"}}/>
              </div>
            </div>
            <p className="text-xs font-medium px-1" style={{color:theme?.primary}}>
              {rule.days?.join(", ")} at {rule.time} · {rule.duration||60} min · {repeatLabel[rule.repeat||"every_week"]}
            </p>
            {clashes.length>0&&(
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{background:"#fef2f2"}}>
                <Icon name="alert" size={13} className="text-red-500"/>
                <p className="text-xs text-red-600">Conflict with: <strong>{clashes.join(", ")}</strong></p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const PackageTemplateForm=({theme,currency,profile,onAdd})=>{
  const isDark=theme?.dark;
  const mc=isDark?"#94a3b8":"#6b7280";
  const bc=isDark?"#334155":"#e5e7eb";
  const [open,setOpen]=useState(false);
  const [name,setName]=useState("");
  const [style,setStyle]=useState("sessions");
  const [sessions,setSessions]=useState("");
  const [days,setDays]=useState("");
  const [amount,setAmount]=useState("");
  const [err,setErr]=useState({});
  const sLabel=profile?.sessionLabel||"Session";

  const reset=()=>{setName("");setStyle("sessions");setSessions("");setDays("");setAmount("");setErr({});};

  const add=()=>{
    const e={};
    if(!name.trim())e.name="Package name is required";
    if((style==="sessions"||style==="both")&&!sessions)e.sessions=`No. of ${sLabel}s is required`;
    if((style==="duration"||style==="both")&&!days)e.days="Duration in days is required";
    if(Object.keys(e).length){setErr(e);return;}
    onAdd({
      id:uid(),
      name:name.trim(),
      packageStyle:style,
      sessions:(style==="sessions"||style==="both")?parseInt(sessions)||0:0,
      durationDays:(style==="duration"||style==="both")?parseInt(days)||0:0,
      amount:parseFloat(amount)||0,
    });
    reset();setOpen(false);
  };

  if(!open)return(
    <button onClick={()=>setOpen(true)}
      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed text-sm font-semibold transition-all w-full justify-center"
      style={{borderColor:isDark?"#334155":"#e5e7eb",color:mc}}>
      <Icon name="plus" size={16}/>Add Package Template
    </button>
  );

  return(
    <div className="rounded-xl border p-4 space-y-3" style={{borderColor:theme?.primary||"#0f766e",background:isDark?"#0f172a":"#f9fafb"}}>
      <p className="text-xs font-semibold uppercase tracking-wider" style={{color:theme?.primary}}>New Template</p>
      <Inp label="Package Name *" value={name} onChange={e=>setName(e.target.value)} theme={theme} error={err.name} placeholder="e.g. Gold Package"/>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Package Type *</label>
        <div className="flex gap-2">
          {[{id:"sessions",label:`By ${sLabel}s`},{id:"duration",label:"By Duration"},{id:"both",label:"Both"}].map(s=>(
            <button key={s.id} type="button" onClick={()=>setStyle(s.id)}
              className="flex-1 py-2 rounded-xl text-xs font-semibold border transition-all"
              style={{background:style===s.id?(theme?.primary||"#0f766e"):"transparent",color:style===s.id?"#fff":(isDark?"#94a3b8":"#6b7280"),borderColor:style===s.id?(theme?.primary||"#0f766e"):bc}}>
              {s.label}
            </button>
          ))}
        </div>
        {style==="both"&&<p className="text-xs" style={{color:mc}}>Expires when either limit is reached.</p>}
      </div>
      {style==="both"&&(
        <div className="flex gap-3">
          <div className="flex flex-col gap-1" style={{width:"80px"}}>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>{`No. of ${sLabel}s *`}</label>
            <input type="text" inputMode="numeric" maxLength={3}
              className="border rounded-xl px-2 py-2.5 text-sm focus:outline-none text-center font-semibold"
              style={{background:isDark?"#0f172a":"#f9fafb",borderColor:err.sessions?"#ef4444":(isDark?"#334155":"#e5e7eb"),color:isDark?"#f1f5f9":"#1f2937"}}
              placeholder="20" value={sessions}
              onChange={e=>setSessions(e.target.value.replace(/[^0-9]/g,""))}/>
            {err.sessions&&<p className="text-xs text-red-500">⚠ {err.sessions}</p>}
          </div>
          <div className="flex flex-col gap-1" style={{width:"80px"}}>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Days *</label>
            <input type="text" inputMode="numeric" maxLength={4}
              className="border rounded-xl px-2 py-2.5 text-sm focus:outline-none text-center font-semibold"
              style={{background:isDark?"#0f172a":"#f9fafb",borderColor:err.days?"#ef4444":(isDark?"#334155":"#e5e7eb"),color:isDark?"#f1f5f9":"#1f2937"}}
              placeholder="30" value={days}
              onChange={e=>setDays(e.target.value.replace(/[^0-9]/g,""))}/>
            {err.days&&<p className="text-xs text-red-500">⚠ {err.days}</p>}
          </div>
        </div>
      )}
      {style==="sessions"&&(
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>{`No. of ${sLabel}s *`}</label>
          <input type="text" inputMode="numeric" maxLength={3}
            className="border rounded-xl px-2 py-2.5 text-sm focus:outline-none text-center font-semibold"
            style={{background:isDark?"#0f172a":"#f9fafb",borderColor:err.sessions?"#ef4444":(isDark?"#334155":"#e5e7eb"),color:isDark?"#f1f5f9":"#1f2937",width:"80px"}}
            placeholder="20" value={sessions}
            onChange={e=>setSessions(e.target.value.replace(/[^0-9]/g,""))}/>
          {err.sessions&&<p className="text-xs text-red-500">⚠ {err.sessions}</p>}
        </div>
      )}
      {style==="duration"&&(
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>Duration (days) *</label>
          <input type="text" inputMode="numeric" maxLength={4}
            className="border rounded-xl px-2 py-2.5 text-sm focus:outline-none text-center font-semibold"
            style={{background:isDark?"#0f172a":"#f9fafb",borderColor:err.days?"#ef4444":(isDark?"#334155":"#e5e7eb"),color:isDark?"#f1f5f9":"#1f2937",width:"80px"}}
            placeholder="30" value={days}
            onChange={e=>setDays(e.target.value.replace(/[^0-9]/g,""))}/>
          {err.days&&<p className="text-xs text-red-500">⚠ {err.days}</p>}
        </div>
      )}
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <Inp label={`Amount (${currency}) — optional`} type="text" inputMode="decimal"
            value={amount} onChange={e=>setAmount(e.target.value.replace(/[^0-9.]/g,""))}
            placeholder="e.g. 5000" theme={theme}/>
        </div>
      </div>
      <div className="flex gap-2">
        <Btn variant="secondary" onClick={()=>{reset();setOpen(false);}} theme={theme}>Cancel</Btn>
        <Btn onClick={add} theme={theme}><Icon name="check" size={16}/>Add Template</Btn>
      </div>
    </div>
  );
};
